/**
 * Billing & invoices (§7.2).
 *
 * Everything on this screen is a charge the platform itself makes — bot creation,
 * the listing subscription, generation fees, credit packs. Transactional language
 * is correct here precisely because the platform is the merchant; it is never
 * correct for a rental, which the platform does not process (§9.1, §7.5).
 */

import { useApi } from '../../hooks/useApi';
import type { BillingSummary } from '../../api/types';
import { Card, Badge, Loading } from '../../components/ui';
import { ErrorView } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { Banner } from '../../components/ui';
import { amountLabel, humanize, localDate, localDateTime } from '../../lib/format';
import { routes } from '../../api/routes';

export function Billing() {
  const billing = useApi<BillingSummary>(routes.me.billing);

  if (billing.initialLoading) return <Loading label="Loading billing" />;
  if (billing.problem) return <ErrorView problem={billing.problem} onRetry={billing.reload} />;
  if (!billing.data) return null;

  const b = billing.data;
  const pastDue = b.subscriptions.filter((s) => s.status === 'past_due');

  return (
    <div className="billing">
      {b.payment_method.failing ? (
        <Banner tone="bad" title="Your payment method is failing" role="alert">
          Charges are being declined. Update the card to keep listings live.
        </Banner>
      ) : null}

      {pastDue.length > 0 ? (
        <Banner tone="warn" title={`${pastDue.length} listing subscription(s) past due`}>
          A listing whose subscription lapses is taken out of the marketplace.
        </Banner>
      ) : null}

      <Card title="Payment method">
        {b.payment_method.present ? (
          // The card details live with the payment provider, not here (§11.1), so
          // brand and last4 may legitimately be unknown. Saying "a card is on
          // file" beats rendering "ending" with nothing after it.
          b.payment_method.brand && b.payment_method.last4 ? (
            <p>
              {b.payment_method.brand} ending {b.payment_method.last4}
              {b.payment_method.exp ? `, expires ${b.payment_method.exp}` : ''}
            </p>
          ) : (
            <p>A card is on file.</p>
          )
        ) : (
          <p className="muted">No card on file. One is needed before the platform can charge you.</p>
        )}
        <button type="button" className="btn">
          {b.payment_method.present ? 'Update card' : 'Add a card'}
        </button>
      </Card>

      <Card title="Listing subscriptions">
        {b.subscriptions.length === 0 ? (
          <EmptyState heading="No subscriptions" body="A listing subscription starts when you list a bot." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Bot</th>
                <th scope="col">Status</th>
                <th scope="col">Amount</th>
                <th scope="col">Next charge</th>
              </tr>
            </thead>
            <tbody>
              {b.subscriptions.map((s) => (
                <tr key={s.id}>
                  <td>{s.bot_name}</td>
                  <td>
                    <Badge tone={s.status === 'active' ? 'ok' : s.status === 'past_due' ? 'bad' : 'neutral'}>
                      {humanize(s.status)}
                    </Badge>
                  </td>
                  <td>{amountLabel(s.amount)} / {s.interval}</td>
                  <td>{s.cancelled_at ? 'cancelled' : localDate(s.current_period_end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Invoices">
        {b.invoices.data.length === 0 ? (
          <EmptyState heading="No invoices yet" body="Charges appear here as soon as they settle." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Description</th>
                <th scope="col">Amount</th>
                <th scope="col">Status</th>
                <th scope="col">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {b.invoices.data.map((inv) => (
                <tr key={inv.id}>
                  <td>{localDateTime(inv.created_at)}</td>
                  <td>
                    {inv.description}
                    {inv.failure_reason ? (
                      <span className="failure-reason"> {inv.failure_reason}</span>
                    ) : null}
                  </td>
                  <td>{amountLabel(inv.amount)}</td>
                  <td>
                    <Badge
                      tone={
                        inv.status === 'succeeded' ? 'ok' : inv.status === 'failed' ? 'bad' : 'neutral'
                      }
                    >
                      {humanize(inv.status)}
                    </Badge>
                  </td>
                  <td>
                    {inv.receipt_url ? (
                      <a href={inv.receipt_url} target="_blank" rel="noreferrer noopener">
                        Open
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
